export const CHARLI_SESSION_VERSION = 1;
export const CHARLI_SESSION_KEY = "chamfr:charli:conversation:v1";
export const CHARLI_SESSION_STORE_VERSION = 2;
export const CHARLI_SESSION_LIMIT = 5;
export const CHARLI_INTERRUPTED_RETRY_MESSAGE = "Something went wrong. Could you search the query again?";

export function normalizeSessionQuery(query) {
  return String(query || "").trim();
}

export function getStandaloneSessionPlan(restoreResult, navigationType = "navigate") {
  if (restoreResult?.restored) {
    return { autoSend: false, prefill: false, writable: true };
  }

  const historyTraversal = navigationType === "back_forward" || navigationType === "reload";
  return {
    autoSend: !historyTraversal,
    prefill: historyTraversal,
    writable: !historyTraversal,
  };
}

function isMessage(message) {
  return !!(
    message &&
    typeof message.id === "string" &&
    ["user", "assistant", "tool"].includes(message.role) &&
    Array.isArray(message.parts)
  );
}

export function interruptSessionMessages(messages, assistantMessageId = null, fallbackId = "charli-interrupted") {
  const interrupted = JSON.parse(JSON.stringify(Array.isArray(messages) ? messages : []));
  const assistantIndex = assistantMessageId
    ? interrupted.findIndex(message => message.id === assistantMessageId && message.role === "assistant")
    : (interrupted.at(-1)?.role === "assistant" ? interrupted.length - 1 : -1);

  if (assistantIndex < 0) {
    interrupted.push({
      id: fallbackId,
      role: "assistant",
      parts: [{ type: "text", text: CHARLI_INTERRUPTED_RETRY_MESSAGE }],
      metadata: { charliOutcome: "interrupted" },
    });
    return interrupted;
  }

  const assistant = interrupted[assistantIndex];
  const hasText = assistant.parts.some(part => part.type === "text" && String(part.text || "").trim());
  const hasRetryMessage = assistant.parts.some(part =>
    part.type === "text" && String(part.text || "").includes(CHARLI_INTERRUPTED_RETRY_MESSAGE)
  );
  assistant.parts = hasText
    ? [
      ...assistant.parts,
      ...(hasRetryMessage ? [] : [{ type: "text", text: `\n\n${CHARLI_INTERRUPTED_RETRY_MESSAGE}` }]),
    ]
    : [{ type: "text", text: CHARLI_INTERRUPTED_RETRY_MESSAGE }];
  assistant.metadata = { ...assistant.metadata, charliOutcome: "interrupted" };
  return interrupted;
}

export function createSessionSnapshot(query, messages, requestActive, savedAt = Date.now()) {
  return {
    version: CHARLI_SESSION_VERSION,
    query: normalizeSessionQuery(query),
    savedAt,
    requestActive: !!requestActive,
    messages: JSON.parse(JSON.stringify(Array.isArray(messages) ? messages : [])),
  };
}

export function serializeSessionSnapshot(snapshot) {
  return JSON.stringify(snapshot);
}

export function restoreSessionSnapshot(serialized, query) {
  let snapshot;
  try {
    snapshot = typeof serialized === "string" ? JSON.parse(serialized) : serialized;
  } catch {
    return { restored: false, clear: true, reason: "invalid", messages: [], interrupted: false };
  }

  if (
    !snapshot ||
    snapshot.version !== CHARLI_SESSION_VERSION ||
    !Array.isArray(snapshot.messages) ||
    !snapshot.messages.every(isMessage)
  ) {
    return { restored: false, clear: true, reason: "invalid", messages: [], interrupted: false };
  }

  if (normalizeSessionQuery(snapshot.query) !== normalizeSessionQuery(query)) {
    return { restored: false, clear: false, reason: "query-mismatch", messages: [], interrupted: false };
  }

  const messages = JSON.parse(JSON.stringify(snapshot.messages));
  if (!messages.length) {
    return { restored: false, clear: false, reason: "empty", messages: [], interrupted: false };
  }

  let interrupted = false;
  if (snapshot.requestActive) {
    interrupted = true;
    return {
      restored: true,
      clear: false,
      reason: "restored",
      messages: interruptSessionMessages(
        messages,
        null,
        `charli-interrupted-${String(snapshot.savedAt || "session")}`,
      ),
      interrupted,
    };
  }

  return { restored: true, clear: false, reason: "restored", messages, interrupted };
}

export function readSessionSnapshot(storage, key, conversationId, query) {
  try {
    const serialized = storage?.getItem(key);
    if (!serialized) return { restored: false, clear: false, reason: "missing", messages: [], interrupted: false };
    const stored = JSON.parse(serialized);
    if (stored?.version === CHARLI_SESSION_VERSION && Array.isArray(stored.messages)) {
      const restored = restoreSessionSnapshot(stored, query);
      if (restored.clear) storage.removeItem(key);
      return restored;
    }
    if (stored?.version !== CHARLI_SESSION_STORE_VERSION || !Array.isArray(stored.conversations)) {
      storage.removeItem(key);
      return { restored: false, clear: true, reason: "invalid", messages: [], interrupted: false };
    }
    const entry = stored.conversations.find(item => item?.conversationId === conversationId);
    if (!entry) return { restored: false, clear: false, reason: "missing", messages: [], interrupted: false };
    const restored = restoreSessionSnapshot(entry.snapshot, query);
    if (restored.clear) {
      stored.conversations = stored.conversations.filter(item => item?.conversationId !== conversationId);
      storage.setItem(key, JSON.stringify(stored));
    }
    return restored;
  } catch {
    return { restored: false, clear: false, reason: "storage-error", messages: [], interrupted: false };
  }
}

export function writeSessionSnapshot(storage, key, conversationId, snapshot, limit = CHARLI_SESSION_LIMIT) {
  try {
    const serialized = storage?.getItem(key);
    let conversations = [];
    if (serialized) {
      const stored = JSON.parse(serialized);
      if (stored?.version === CHARLI_SESSION_STORE_VERSION && Array.isArray(stored.conversations)) {
        conversations = stored.conversations;
      }
    }
    conversations = conversations
      .filter(entry => entry?.conversationId && entry.conversationId !== conversationId)
      .concat({ conversationId, snapshot })
      .slice(-Math.max(1, limit));
    storage?.setItem(key, JSON.stringify({
      version: CHARLI_SESSION_STORE_VERSION,
      conversations,
    }));
    return true;
  } catch {
    return false;
  }
}
